import type React from 'react';
import { useSelector } from 'react-redux';

import GeofenceBadge from './GeofenceBadge';
import IdleEngineContent from './IdleEngineContent';
import * as styles from './styles';

import { isWebDataSuccess } from '@cobliteam/api-lib';

import { format, formatDuration } from '@cobliteam/utils/date';

import { selectAddress } from '@cobliteam/store';

import { usePermissions } from '@cobliteam/hooks';

import Typography from '@cobliteam/ui/components/Typography';
import {
  TrackingPopUp,
  TrackingPopUpContent,
  TrackingPopUpFooter,
  TrackingPopUpHeader,
  TrackingPopUpHeaderMain,
  TrackingPopUpInfoRow,
  TrackingPopUpInfoRowText,
  TrackingPopUpSkeleton,
  TrackingPopUpTitle,
} from '../Tracking/components/Common/TrackingPopUp';
import { StopIconComponent } from '../Tracking/components/Map/MapIcons/StopIcon/StopIcon';

import type { GeofenceCategory } from '@cobliteam/common-types';
import TrackingPermissions from '../../permissions';
import type { ProcessedStop, ProcessedStopRouteStep } from '../../store';

export interface StopPopupProps {
  stop: ProcessedStop | ProcessedStopRouteStep;
  idleTimeHighlighted?: boolean;
  filteredDuration?: number;
  children?: React.ReactNode;
}

export const StopPopup = ({ stop, children, idleTimeHighlighted, filteredDuration }: StopPopupProps) => {
  const { isAccessAllowed } = usePermissions();
  const isAllowedViewGeofences = isAccessAllowed(TrackingPermissions.VIEW_PLACES_OF_INTEREST);
  const addressWebData = useSelector(selectAddress);

  const stopNumber = stop.index + 1;
  const address = isWebDataSuccess(addressWebData) ? addressWebData.data : <TrackingPopUpSkeleton />;
  const dateText = `${format(stop.startTime, "dd/MM/yyyy 'de' HH:mm")} às ${format(stop.endTime, 'HH:mm')}`;

  const stopDuration = stop.endTime - stop.startTime;
  const hasGeofence = stop.geofences.length > 0;
  const selectedGeofence = stop.geofences[0];
  const stopIconOptions = {
    hasGeofence,
    isAllowedToViewPlacesOfInterest: isAllowedViewGeofences,
  };

  return (
    <TrackingPopUp>
      <TrackingPopUpHeader>
        <TrackingPopUpHeaderMain>
          <StopIconComponent index={stopNumber - 1} options={stopIconOptions} />
          <TrackingPopUpTitle>Parada {stopNumber} do trajeto</TrackingPopUpTitle>
        </TrackingPopUpHeaderMain>
        {hasGeofence && <GeofenceBadge category={(selectedGeofence.category as GeofenceCategory) || 'default'} />}
      </TrackingPopUpHeader>
      <TrackingPopUpContent>
        <TrackingPopUpInfoRowText title='Data'>{dateText}</TrackingPopUpInfoRowText>
        {hasGeofence && <TrackingPopUpInfoRowText title='Local de interesse'>{selectedGeofence.name}</TrackingPopUpInfoRowText>}
        <TrackingPopUpInfoRowText title='Endereço'>{address}</TrackingPopUpInfoRowText>
        <TrackingPopUpInfoRowText title='Motorista'>{stop.driverName || 'Sem motorista'}</TrackingPopUpInfoRowText>
        {stop.idleOnInMillis > 0 && (
          <TrackingPopUpInfoRow title='Motor ocioso'>
            <IdleEngineContent stop={stop} idleTimeHighlighted={idleTimeHighlighted}>
              {formatDuration(stop.idleOnInMillis, true)}
            </IdleEngineContent>
          </TrackingPopUpInfoRow>
        )}
        <TrackingPopUpInfoRowText title='Duração'>{formatDuration(stopDuration, true) || '---'}</TrackingPopUpInfoRowText>

        {filteredDuration && (
          <div className={styles.filteredHighlight}>
            <Typography variant='description-list-default'>
              Você está vendo <b>{formatDuration(filteredDuration)}</b> do tempo total da parada
            </Typography>
          </div>
        )}
      </TrackingPopUpContent>
      <TrackingPopUpFooter>{children}</TrackingPopUpFooter>
    </TrackingPopUp>
  );
};
